import { IoPerson } from 'react-icons/io5'
import colors from 'tailwindcss/colors'
import { Select } from '../form/Select'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useToast } from '@/hooks/useToast'
import { useDispatch } from 'react-redux'
import { handleSetUsers } from '@/storage/modules/users/reducer'

import { api } from '@/services/api'
import { AccountStatus, UserDataProps } from '@/types/userProps'
import { EmptyState } from '../EmptyState'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'

function formatLastSeen(value?: string | null) {
  if (!value) {
    return null
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

const statusOptions: { label: string; value: AccountStatus }[] = [
  { label: 'Pending Verification', value: 'PENDING_VERIFICATION' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Disabled', value: 'SUSPENDED' },
]

function toAccountStatus(value: string): AccountStatus | null {
  return statusOptions.some((statusOption) => statusOption.value === value)
    ? (value as AccountStatus)
    : null
}

function formatTokenPreview(token: string) {
  if (token.length <= 20) return token

  return `${token.slice(0, 10)}...${token.slice(-8)}`
}

export function Users() {
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const [usersList, setUsersList] = useState<UserDataProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const canLoadMoreRef = useRef(false)

  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchUsersPage = useCallback(async (page: number) => {
    const response = await api.get<PaginatedResponseProps<UserDataProps>>(
      '/users',
      {
        params: {
          page,
        },
      },
    )

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      users: response.data.data,
      pagination,
    }
  }, [])

  const syncUsersStore = useCallback(async () => {
    const usersResponse = await api.get('/users', {
      params: {
        page: 1,
      },
    })

    dispatch(
      handleSetUsers({ users: usersResponse.data.data as UserDataProps[] }),
    )
  }, [dispatch])

  const handleLoadFirstPage = useCallback(async () => {
    try {
      setIsLoadingInitial(true)

      const response = await handleFetchUsersPage(1)
      setUsersList(response.users)
      setPaginationMeta(response.pagination)
      await syncUsersStore()
    } catch (error) {
      showToast('Error fetching users', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoadingInitial(false)
    }
  }, [handleFetchUsersPage, showToast, syncUsersStore])

  const handleChangeUserStts = useCallback(
    async (
      userId: string,
      selectedStatus: string,
      currentStatus: AccountStatus,
    ) => {
      const nextStatus = toAccountStatus(selectedStatus)

      if (!nextStatus) {
        showToast('Invalid status selected', {
          type: 'warning',
          theme: 'light',
        })
        return
      }

      if (nextStatus === currentStatus) {
        return
      }

      try {
        await api.patch(`/users/${userId}/status`, {
          accountStatus: nextStatus,
        })
        await syncUsersStore()
        setUsersList((prev) =>
          prev.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  accountStatus: nextStatus,
                }
              : user,
          ),
        )

        showToast('User status updated successfully', {
          type: 'success',
          theme: 'light',
        })
      } catch (error) {
        showToast('Error updating user status', {
          type: 'error',
          theme: 'light',
        })
      }
    },
    [showToast, syncUsersStore],
  )

  const handleCopyClick = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      showToast('Text copied to clipboard', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      showToast('Error copying to clipboard', {
        type: 'warning',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    handleLoadFirstPage()
  }, [handleLoadFirstPage])

  useEffect(() => {
    paginationMetaRef.current = paginationMeta
    canLoadMoreRef.current = canLoadMore
  }, [canLoadMore, paginationMeta])

  useEffect(() => {
    const loadMoreUsers = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchUsersPage(nextPage)
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        setUsersList((prev) => [...prev, ...response.users])
        setPaginationMeta(response.pagination)
      } catch (error) {
        showToast('Error fetching more users', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (!canLoadMoreRef.current || isLoadingMoreRef.current) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreUsers()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleFetchUsersPage, showToast])

  return (
    <>
      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {usersList.length} of {paginationMeta.totalItems} users
        </p>
      </div>

      {usersList.length === 0 && !isLoadingInitial && (
        <EmptyState
          title="No users yet"
          description="Users will appear here as soon as accounts are created."
        />
      )}
      {usersList.map((user) => {
        const userName =
          user.name?.trim() || user.displayName?.trim() || 'No name'
        const userEmail = user.email?.trim() || 'No email'
        const userPrimaryDevice = user.device ?? user.devices?.[0]
        const userTokenFcm =
          userPrimaryDevice?.fcmToken?.trim() || user.tokenFcm?.trim()
        const userLastSeen = formatLastSeen(userPrimaryDevice?.lastSeenAt)

        return (
          <div
            key={user.id}
            className="mt-6 w-full rounded-2xl border bg-white p-6 transition-colors hover:border-gray-300 base:p-5 md:p-4"
          >
            <div className="flex items-start justify-between gap-6 base:flex-col">
              <div className="flex min-w-0 flex-1 items-start gap-4 md:flex-col md:gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gray-700">
                  {user.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt="user photo profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center">
                      <IoPerson size={30} color={colors.white} />
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                      Name
                    </span>
                    <p className="truncate text-lg font-bold text-purple-600">
                      {userName}
                    </p>
                  </div>

                  <div className="mt-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                      Email
                    </span>
                    <p className="mt-1 break-all text-sm text-gray-500">
                      {userEmail}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex w-full max-w-[220px] shrink-0 flex-col gap-3 base:max-w-none">
                <Select
                  label="Status"
                  options={statusOptions}
                  onChange={(e) =>
                    handleChangeUserStts(
                      user.id,
                      e.currentTarget.value,
                      user.accountStatus,
                    )
                  }
                  value={user.accountStatus}
                />

                {userLastSeen && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                      Last seen
                    </span>
                    <p className="mt-1 text-sm font-semibold text-gray-700">
                      {userLastSeen}
                    </p>
                  </div>
                )}

                {userTokenFcm && (
                  <button
                    title="copy token"
                    className="rounded-xl border border-purple-200 px-4 py-3 text-left text-sm font-semibold text-purple-700 transition hover:bg-purple-50"
                    onClick={() => handleCopyClick(userTokenFcm)}
                  >
                    <span className="block">Token FCM</span>
                    <span className="mt-1 block text-xs font-normal text-purple-500">
                      {formatTokenPreview(userTokenFcm)}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {(isLoadingInitial || isLoadingMore) && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>
            {isLoadingInitial ? 'Loading users...' : 'Loading more users...'}
          </p>
        </div>
      )}
    </>
  )
}
